import { booleanAttribute, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '../interface';
import { GalaxyFilterOperator } from '../enums';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { BehaviorSubject, combineLatest, debounceTime, startWith } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type SelectionType = 'single' | 'range';

@Component({
  selector: 'glxy-float-filter-input',
  templateUrl: './float-filter-input.html',
  styleUrls: ['./float-filter-input.scss'],
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyFloatFilterInputComponent) }],
  imports: [CommonModule, TranslateModule, MatInputModule, GalaxyFormFieldModule, ReactiveFormsModule],
})
export class GalaxyFloatFilterInputComponent implements GalaxyAbstractFilterInput {
  @Input() set operator(value: GalaxyFilterOperator) {
    this.setSelectionType(value);
  }

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length === 1) {
      this.setValue(value[0]);
    } else if (value?.length === 2) {
      this.setRangeValue(value[0], value[1]);
    }
  }

  @Input({ transform: booleanAttribute }) set disabled(value: boolean) {
    if (value) {
      this.valueControl.disable();
      this.endValueControl.disable();
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  readonly selectionType$$ = new BehaviorSubject<SelectionType>('single');
  readonly valueControl = new FormControl<number | undefined>(undefined, { nonNullable: true });
  readonly endValueControl = new FormControl<number | undefined>(undefined, { nonNullable: true });

  constructor() {
    combineLatest([
      this.selectionType$$,
      this.valueControl.valueChanges.pipe(startWith(undefined)),
      this.endValueControl.valueChanges.pipe(startWith(undefined)),
    ])
      .pipe(takeUntilDestroyed(), debounceTime(100))
      .subscribe(([selectionType, value, endValue]) => {
        if (selectionType === 'single') {
          this.valueChanges.emit([{ float: value }]);
        } else {
          this.valueChanges.emit([{ float: value }, { float: endValue }]);
        }
      });
  }

  clearValue() {
    this.valueControl.reset();
    this.endValueControl.reset();
  }

  private setSelectionType(operator: GalaxyFilterOperator): void {
    const rangeOperators = [
      GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN,
      GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_BETWEEN,
    ];
    this.selectionType$$.next(rangeOperators.includes(operator) ? 'range' : 'single');
  }

  private setValue(value: GalaxyFilterValueInterface): void {
    this.valueControl.setValue(value.float);
    this.endValueControl.setValue(undefined);
  }

  private setRangeValue(startValue: GalaxyFilterValueInterface, endValue: GalaxyFilterValueInterface): void {
    this.valueControl.setValue(startValue.float);
    this.endValueControl.setValue(endValue.float);
  }
}
